import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from './ui/badge';
import { experience } from '../data/portfolio';
import { Briefcase, GraduationCap, ChevronRight } from 'lucide-react';

import ParticleField from './ParticleField';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' }
  })
};

const TimelineItem = ({ item, index, isLast }) => {
  const isWork = item.type === 'work';

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      custom={index}
      viewport={{ once: true }}
      className="relative flex gap-6"
    >
      {/* Timeline line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center z-10"
          style={{
            background: isWork ? 'hsl(var(--primary) / 0.1)' : 'hsl(var(--secondary) / 0.1)',
            border: `1px solid ${isWork ? 'hsl(var(--primary) / 0.3)' : 'hsl(var(--secondary) / 0.3)'}`
          }}
        >
          {isWork
            ? <Briefcase size={16} style={{ color: 'hsl(var(--primary))' }} />
            : <GraduationCap size={16} style={{ color: 'hsl(var(--secondary))' }} />
          }
        </div>
        {!isLast && (
          <div
            className="w-px flex-1 mt-2"
            style={{ background: 'linear-gradient(to bottom, hsl(var(--border)), transparent)' }}
          />
        )}
      </div>

      {/* Content */}
      <div className="pb-10 flex-1 min-w-0">
        <div className="glass rounded-2xl p-6 hover:border-primary/20 transition-colors duration-300">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-heading font-bold text-foreground text-base">{item.role}</h3>
              <p className="text-muted-foreground text-sm mt-0.5">
                <span style={{ color: isWork ? 'hsl(var(--primary))' : 'hsl(var(--secondary))' }}>
                  {item.company}
                </span>
                {item.client && (
                  <span className="text-muted-foreground"> · {item.client}</span>
                )}
              </p>
            </div>
            <Badge
              variant="outline"
              className="text-xs font-mono border-border/60 text-muted-foreground flex-shrink-0"
            >
              {item.period}
            </Badge>
          </div>

          <p className="text-muted-foreground text-sm leading-relaxed mb-4">
            {item.description}
          </p>

          {/* Highlights */}
          <ul className="space-y-1.5 mb-4">
            {item.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <ChevronRight size={12} className="mt-0.5 flex-shrink-0" style={{ color: isWork ? 'hsl(var(--primary))' : 'hsl(var(--secondary))' }} />
                {h}
              </li>
            ))}
          </ul>

          {/* Tech tags */}
          <div className="flex flex-wrap gap-1.5">
            {item.tech.map((t) => (
              <span
                key={t}
                className="font-mono text-xs px-2 py-0.5 rounded-md border border-border/60 text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ExperienceSection = () => {
  const workItems = experience.filter(e => e.type === 'work');
  const educationItems = experience.filter(e => e.type === 'education');

  return (
    <section id="experience" className="relative py-24 px-6 md:px-12 lg:px-24">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 80% 50%, hsl(var(--primary) / 0.03) 0%, transparent 60%)'
        }}
      />
       {/* Grid dots */}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <Badge variant="outline" className="border-primary/30 text-primary bg-primary/5 mb-4 font-mono text-xs uppercase tracking-wider">
            Journey
          </Badge>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground leading-tight">
            Experience &
            <span className="gradient-text"> Education</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Work Experience */}
          <div>
            <motion.h3
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="font-heading font-semibold text-foreground text-lg mb-8 flex items-center gap-2"
            >
              <Briefcase size={18} style={{ color: 'hsl(var(--primary))' }} />
              Work Experience
            </motion.h3>
            <div>
              {workItems.map((item, i) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  index={i}
                  isLast={i === workItems.length - 1}
                />
              ))}
            </div>
          </div>

          {/* Education */}
          <div>
            <motion.h3
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="font-heading font-semibold text-foreground text-lg mb-8 flex items-center gap-2"
            >
              <GraduationCap size={18} style={{ color: 'hsl(var(--secondary))' }} />
              Education
            </motion.h3>
            <div>
              {educationItems.map((item, i) => (
                <TimelineItem
                  key={item.id}
                  item={item}
                  index={i}
                  isLast={i === educationItems.length - 1}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
